/**
 * Small presentational building blocks for the hotel checkout form: the
 * accordion `CollapsibleFormSection` and the labelled `Field` wrapper.
 * `SectionId` enumerates the accordion's sections. (The quantity `Stepper`
 * now lives in `#ui` since it's reusable outside checkout.)
 */

import React from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { Button, Label } from '#ui/index.js';
import { MaterialIcon } from '#ui/MaterialIcon.js';

export type SectionId = 'your-details' | 'room-prefs' | 'special-requests' | 'payment';

/**
 * Collapsible accordion section for the checkout form. Only one section is
 * open at a time (controlled by the parent's `openSection`). The header
 * itself is read-only — forward navigation happens via the `Next` button in
 * `footer`; backward navigation happens via the "Edit" button shown on
 * completed (and collapsed) sections.
 */
export function CollapsibleFormSection({
  title,
  description,
  summary,
  complete,
  isOpen,
  onEdit,
  children,
  footer,
}: {
  title: string;
  description?: string;
  /** One-line read-only summary shown when collapsed. */
  summary?: React.ReactNode;
  /** When true, shows a check icon in the header (collapsed view). */
  complete?: boolean;
  isOpen: boolean;
  /** Called when the user clicks the Edit button on a completed, collapsed section. */
  onEdit?: () => void;
  children: React.ReactNode;
  /** Rendered below `children` when open — typically a `Next` button. */
  footer?: React.ReactNode;
}): React.ReactNode {
  const showEdit = !!(!isOpen && complete && onEdit);
  return (
    <section className={cn('rounded-md border bg-background', isOpen && 'shadow-sm')}>
      <div className={cn('flex items-center gap-3 px-6 py-6', isOpen && 'pb-4')}>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-base md:text-lg font-display">{title}</h2>
          </div>
          {!isOpen && complete && summary ? <div className="mt-1 text-sm text-muted-foreground">{summary}</div> : null}
        </div>
        {showEdit ? (
          <Button variant="icon" size="icon" onClick={onEdit} className="shrink-0">
            <MaterialIcon icon="edit" />
          </Button>
        ) : null}
      </div>
      {isOpen ? (
        <div className="px-6 pb-6">
          {description ? <p className="mb-4 text-sm text-pretty text-muted-foreground">{description}</p> : null}
          {children}
          {footer ? <div className="mt-6 flex justify-end">{footer}</div> : null}
        </div>
      ) : null}
    </section>
  );
}

export function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}): React.ReactNode {
  return (
    <div>
      <Label className="mb-1 block">
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </Label>
      {children}
    </div>
  );
}
